import { LitElement, html } from 'lit';
import { onAuthStateChanged } from 'firebase/auth';
import { createContext, provide } from '@lit/context';
import { auth } from '../client/FirebaseClient';
import { API_ENDPOINT } from '../constants';
import { property } from 'lit/decorators.js';

export interface AuthState {
  user: { uid: string, plan: 'default' | 'plus' | 'pro' } | null | undefined;
  isAnonymous: boolean;
}

export const AuthContext = createContext('auth-context');

class AuthProvider extends LitElement {
  @provide({ context: AuthContext })
  @property({ attribute: false })
  public state: AuthState = { user: undefined, isAnonymous: false };

  private refreshing = false;

  constructor() {
    super();
    this.state = { user: undefined, isAnonymous: false };
  }

  connectedCallback() {
    super.connectedCallback();

    // Initial bootstrap
    this.refresh();

    // Firebase auth state → reflect sign-in/out
    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.state = { user: null, isAnonymous: false };
        return;
      }

      if (user.isAnonymous) {
        this.state = { ...this.state, isAnonymous: true };
      }
      // Give backend a moment to set cookies (after notifyBackend)
      await new Promise(r => setTimeout(r, 150));
      await this.refresh();
    });

    // Allow other components (LoginModal) to force-refresh
    window.addEventListener('auth:refresh', () => {
      this.refresh()
    });
  }

  async refresh() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      const res = await fetch(`${API_ENDPOINT}/auth-state`, { credentials: 'include' });
      if (!res.ok) throw new Error('auth-state failed');
      const data = await res.json();
      if (data?.user_id) {
        this.state = {
          ...this.state,
          user: { uid: data.user_id, plan: data.plan || 'default' }
        };

        try {
          const next = localStorage.getItem('post_login_redirect');
          if (next) {
            localStorage.removeItem('post_login_redirect');
            window.location.href = next;
          }
        } catch { }
      } else {
        this.state = { ...this.state, user: null };
      }
    } catch {
      this.state = { ...this.state, user: null };
    } finally {
      this.refreshing = false;
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}

if (!customElements.get('auth-provider')) {
  customElements.define('auth-provider', AuthProvider);
}

